import type { useDisclosureWithContext } from '@talos/kyoko';
import {
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  Dialog,
  DialogFooterWrapper,
  Flex,
  FormControlSizes,
  FormGroup,
  IconName,
  Input,
  Stack,
  ToggleButton,
  useCallbackRef,
} from '@talos/kyoko';
import { useCallback, useState, type ReactNode } from 'react';
import { MarketSelector } from '../../../MarketSelector/MarketSelector';
import type { MarketSelectorItem, MarketSelectorPreset } from '../../../MarketSelector/types';
import { useMarketSelector } from '../../../MarketSelector/useMarketSelector';
import { EMPTY_MARKET_SELECTOR_VALUE } from '../../../MarketSelector/utils/OrderMarketsField';
import type { PriceGridLegMarketDataColumnSettings, PriceGridMarketDataColumnSettings } from './types';

export interface PriceGridMarketDataColumnSettingsDialogContext {
  currentSettings: PriceGridMarketDataColumnSettings | undefined;
  save: (newSettings: PriceGridMarketDataColumnSettings | undefined) => void;
  deleteAction?: {
    itemName: string;
    onDelete: () => void;
  };
}

export interface PriceGridLegMarketDataColumnSettingsDialogContext {
  currentSettings: PriceGridLegMarketDataColumnSettings | undefined;
  save: (newSettings: PriceGridLegMarketDataColumnSettings | undefined) => void;
}

type PriceGridMarketDataColumnSettingsDialogProps = ReturnType<
  typeof useDisclosureWithContext<PriceGridMarketDataColumnSettingsDialogContext>
> & {
  aggregationPresets: MarketSelectorPreset[];
  marketSelectorItems: MarketSelectorItem[];
};

type PriceGridLegMarketDataColumnSettingsDialogProps = ReturnType<
  typeof useDisclosureWithContext<PriceGridLegMarketDataColumnSettingsDialogContext>
> & {
  aggregationPresets: MarketSelectorPreset[];
  marketSelectorItems: MarketSelectorItem[];
};

export function PriceGridMarketDataColumnSettingsDialog({
  context,
  aggregationPresets,
  marketSelectorItems,
  ...dialogProps
}: PriceGridMarketDataColumnSettingsDialogProps) {
  if (!context) {
    return null;
  }

  return (
    <PriceGridMarketDataColumnSettingsDialogContent
      key={dialogProps.openId}
      context={context}
      aggregationPresets={aggregationPresets}
      marketSelectorItems={marketSelectorItems}
      {...dialogProps}
    />
  );
}

function PriceGridMarketDataColumnSettingsDialogContent({
  context,
  aggregationPresets,
  marketSelectorItems,
  ...dialogProps
}: Omit<PriceGridMarketDataColumnSettingsDialogProps, 'context'> & {
  context: PriceGridMarketDataColumnSettingsDialogContext;
}) {
  const [title, setTitle] = useState(context.currentSettings?.title ?? '');
  const [marketSelectorValue, setMarketSelectorValue] = useState(context.currentSettings?.marketSelectorValue);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const handleConfirm = useCallbackRef(() => {
    context.save({
      ...context.currentSettings,
      title,
      marketSelectorValue,
    });
  });

  const handleCustomConfirm = useCallbackRef(() => {
    handleConfirm();
    dialogProps.close();
  });

  const marketSelector = useMarketSelector({
    value: marketSelectorValue ?? EMPTY_MARKET_SELECTOR_VALUE,
    items: marketSelectorItems,
    onChange: setMarketSelectorValue,
    presets: aggregationPresets,
  });

  const handleDelete = useCallback(() => {
    setIsDeleteConfirmationOpen(true);
  }, []);

  const handleDeleteConfirm = useCallbackRef(() => {
    context.deleteAction?.onDelete();
    setIsDeleteConfirmationOpen(false);
    dialogProps.close();
  });

  return (
    <>
      <Dialog
        {...dialogProps}
        title="Price Column Settings"
        confirmLabel="Save"
        onConfirm={handleConfirm}
        preventClosing={isDeleteConfirmationOpen}
        width={360}
        alignContent="left"
        dataTestId="price-grid-price-column-settings-dialog"
        customActions={
          context.deleteAction ? (
            <DialogFooterWrapper justifyContent="space-between">
              <Flex flex={1}>
                <Button
                  type="button"
                  startIcon={IconName.Trash}
                  variant={ButtonVariants.Negative}
                  onClick={handleDelete}
                  aria-label="Delete price column"
                  data-testid="price-grid-delete-price-column-button"
                />
              </Flex>
              <Button type="button" onClick={dialogProps.close} data-testid="dialog-cancel">
                Cancel
              </Button>
              <Button
                type="button"
                variant={ButtonVariants.Primary}
                onClick={handleCustomConfirm}
                data-testid="dialog-confirm"
              >
                Save
              </Button>
            </DialogFooterWrapper>
          ) : undefined
        }
      >
        <PriceGridColumnSettingsFields
          title={title}
          titlePlaceholder="Price"
          onTitleChange={setTitle}
          marketSelector={marketSelector}
        />
      </Dialog>
      <Dialog
        isOpen={isDeleteConfirmationOpen}
        close={() => setIsDeleteConfirmationOpen(false)}
        title="Delete Price Column"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant={ButtonVariants.Negative}
        onConfirm={handleDeleteConfirm}
        width={360}
        dataTestId="price-grid-delete-price-column-confirmation-dialog"
      >
        {context.deleteAction ? `Are you sure you want to delete ${context.deleteAction.itemName}?` : null}
      </Dialog>
    </>
  );
}

export function PriceGridLegMarketDataColumnSettingsDialog({
  context,
  aggregationPresets,
  marketSelectorItems,
  ...dialogProps
}: PriceGridLegMarketDataColumnSettingsDialogProps) {
  if (!context) {
    return null;
  }

  return (
    <PriceGridLegMarketDataColumnSettingsDialogContent
      key={dialogProps.openId}
      context={context}
      aggregationPresets={aggregationPresets}
      marketSelectorItems={marketSelectorItems}
      {...dialogProps}
    />
  );
}

function PriceGridLegMarketDataColumnSettingsDialogContent({
  context,
  aggregationPresets,
  marketSelectorItems,
  ...dialogProps
}: Omit<PriceGridLegMarketDataColumnSettingsDialogProps, 'context'> & {
  context: PriceGridLegMarketDataColumnSettingsDialogContext;
}) {
  const [title, setTitle] = useState(context.currentSettings?.title ?? '');
  const [legIndex, setLegIndex] = useState(context.currentSettings?.legIndex);
  const [marketSelectorValue, setMarketSelectorValue] = useState(context.currentSettings?.marketSelectorValue);

  const handleConfirm = useCallbackRef(() => {
    context.save({
      title,
      legIndex,
      marketSelectorValue,
    });
  });

  const marketSelector = useMarketSelector({
    value: marketSelectorValue ?? EMPTY_MARKET_SELECTOR_VALUE,
    items: marketSelectorItems,
    onChange: setMarketSelectorValue,
    presets: aggregationPresets,
  });

  return (
    <Dialog
      {...dialogProps}
      title="Leg Price Column Settings"
      confirmLabel="Save"
      onConfirm={handleConfirm}
      width={360}
      alignContent="left"
      dataTestId="price-grid-leg-price-column-settings-dialog"
    >
      <PriceGridColumnSettingsFields
        title={title}
        titlePlaceholder="Leg Price"
        onTitleChange={setTitle}
        marketSelector={marketSelector}
      >
        <FormGroup label="Leg Index" mb="0">
          <ButtonGroup size={FormControlSizes.Small}>
            <ToggleButton disabled selected={legIndex === 0} onClick={() => setLegIndex(0)}>
              Leg 1
            </ToggleButton>
            <ToggleButton disabled selected={legIndex === 1} onClick={() => setLegIndex(1)}>
              Leg 2
            </ToggleButton>
          </ButtonGroup>
        </FormGroup>
      </PriceGridColumnSettingsFields>
    </Dialog>
  );
}

function PriceGridColumnSettingsFields({
  title,
  titlePlaceholder,
  onTitleChange,
  marketSelector,
  children,
}: {
  title: string;
  titlePlaceholder: string;
  onTitleChange: (title: string) => void;
  marketSelector: ReturnType<typeof useMarketSelector>;
  children?: ReactNode;
}) {
  return (
    <Stack gap="spacing12">
      <FormGroup label="Title" mb="0">
        <Input autoFocus placeholder={titlePlaceholder} value={title} onChange={e => onTitleChange(e.target.value)} />
      </FormGroup>
      {children}
      <FormGroup label="Markets" tooltip="An empty selection defaults to all possible Markets">
        <Box h="200px">
          <MarketSelector marketSelectorAPI={marketSelector} />
        </Box>
      </FormGroup>
    </Stack>
  );
}
